package com.flowyride.core.map

import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.feature
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.layers.Anchor
import org.maplibre.compose.layers.FillLayer
import org.maplibre.compose.sources.VectorSource
import org.maplibre.compose.sources.getBaseSource
import org.maplibre.compose.util.MaplibreComposable

/** Vector source of the OpenFreeMap styles (OpenMapTiles schema). */
private const val BASE_SOURCE_ID = "openmaptiles"

/** First base layer drawn over the land: ours go just under it, so water, roads and labels stay on top. */
private const val FIRST_LAYER_ABOVE_LAND = "water"

/** Built-up areas: the rider can tell a crossing of a village (50 km/h) from an open road. */
private val BuiltUpClasses = listOf("residential", "commercial", "retail", "industrial")
internal val BuiltUpColor = Color(0xFFE6D3C6)

/** Forest and fields: "liberty" barely tints woods and does not draw farmland at all. */
internal val WoodColor = Color(0xFFBFDCA8)
internal val FarmlandColor = Color(0xFFF2EDCB)
internal val GrassColor = Color(0xFFD9EAC4)

/**
 * Makes the landscape readable on the base map: towns, forests and fields, from the country view down to the street.
 * Call it inside the style content of a map using [MAP_STYLE_URL].
 */
@Composable
@MaplibreComposable
internal fun LandscapeLayers() {
    val source = getBaseSource<VectorSource>(BASE_SOURCE_ID) ?: return
    Anchor.Below(FIRST_LAYER_ABOVE_LAND) {
        FillLayer(
            id = "flowy-farmland",
            source = source,
            sourceLayer = "landcover",
            filter = classIn("farmland"),
            color = const(FarmlandColor),
        )
        FillLayer(
            id = "flowy-grass",
            source = source,
            sourceLayer = "landcover",
            filter = classIn("grass"),
            color = const(GrassColor),
        )
        FillLayer(
            id = "flowy-wood",
            source = source,
            sourceLayer = "landcover",
            filter = classIn("wood"),
            color = const(WoodColor),
        )
        // Last: a village in a clearing stays visible.
        FillLayer(
            id = "flowy-built-up",
            source = source,
            sourceLayer = "landuse",
            filter = classIn(*BuiltUpClasses.toTypedArray()),
            color = const(BuiltUpColor),
        )
    }
}

private fun classIn(vararg classes: String): Expression<BooleanValue> =
    switch(feature["class"], *classes.map { case(it, const(true)) }.toTypedArray(), fallback = const(false))
